import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LayoutDashboard,
  Megaphone,
  Users,
  DollarSign,
  Lock,
  ExternalLink,
  Trash2,
  ChevronDown,
  Store,
  X,
  CheckCircle,
  XCircle,
  Zap,
  Loader2,
  Car,
  Search,
  MapPin,
  Upload,
  FileDown,
  Globe,
  Phone,
  Mail,
  Radar,
  Settings,
  FileSignature,
  UserCheck,
  Send,
  Clock,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  MessageSquare,
  Plus,
  History,
} from 'lucide-react';
import { DealerProspectingPanel } from './DealerProspectingPanel';
import { DualAmortizationView } from './DualAmortizationView';
import { FamSpinner } from '../badges/Badge';
import { decodeVin } from '../../lib/nhtsa';
import type { NHTSAVehicle } from '../../lib/nhtsa';
import { COACHING_PROMPTS, renderPrompt } from '../../lib/coachingPrompts';
import type { CoachingPromptId } from '../../lib/coachingPrompts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OverviewStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalRaised: number;
  totalUsers: number;
}

interface RecentContribution {
  id: string;
  car_goal_id: string;
  campaign_title: string;
  amount: number;
  contribution_type: string;
  created_at: string;
}

interface Campaign {
  id: string;
  title: string;
  user_id: string;
  status: string;
  current_amount: number;
  goal_amount: number;
  supporter_count: number;
  created_at: string;
}

interface UserProfile {
  id: string;
  name: string;
  email: string;
  created_at: string;
}

interface Transaction {
  id: string;
  car_goal_id: string;
  campaign_title: string;
  amount: number;
  contribution_type: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// SettingsSection — FAM platform handles (editable from admin)
// ---------------------------------------------------------------------------

const PLATFORM_KEYS = [
  { key: 'fam_handle_twitter',   label: 'X / Twitter',  color: 'text-sky-500' },
  { key: 'fam_handle_instagram', label: 'Instagram',    color: 'text-pink-500' },
  { key: 'fam_handle_facebook',  label: 'Facebook',     color: 'text-blue-600' },
  { key: 'fam_handle_tiktok',    label: 'TikTok',       color: 'text-gray-900' },
  { key: 'fam_handle_youtube',   label: 'YouTube',      color: 'text-red-600' },
] as const;

function SettingsSection() {
  const [handles, setHandles] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/admin?action=get_settings')
      .then(r => r.json())
      .then(data => { if (data.settings) setHandles(data.settings); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (key: string) => {
    setSaving(key);
    try {
      await fetch('/api/admin?action=update_setting', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value: handles[key] ?? '' }),
      });
      setSaved(key);
      setTimeout(() => setSaved(null), 2000);
    } catch { /* non-fatal */ }
    setSaving(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900">Settings</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-1">FAM Social Handles</h3>
          <p className="text-xs text-gray-400 mb-4">These handles appear in pre-drafted share messages shown to creators on their social accounts step.</p>

          {loading ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-3">
              {PLATFORM_KEYS.map(({ key, label, color }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className={`w-28 text-sm font-medium shrink-0 ${color}`}>{label}</span>
                  <input
                    type="text"
                    value={handles[key] ?? ''}
                    onChange={e => setHandles(h => ({ ...h, [key]: e.target.value }))}
                    placeholder="@handle"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                  <button
                    onClick={() => handleSave(key)}
                    disabled={saving === key}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 min-w-[64px] justify-center"
                  >
                    {saved === key ? (
                      <><CheckCircle className="w-3.5 h-3.5" /> Saved</>
                    ) : saving === key ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : 'Save'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CoOwnershipSigningModal — admin-triggered Step 3 contract
// ---------------------------------------------------------------------------

function CoOwnershipSigningModal({ campaign, onClose }: { campaign: Campaign; onClose: () => void }) {
  const [status, setStatus] = useState<'loading' | 'iframe' | 'error'>('loading');
  const [embedUrl, setEmbedUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    fetch('/api/create-signing-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contractType: 'co_ownership',
        signerEmail: 'admin@fam.autos',
        signerName: campaign.title,
        fields: [
          { name: 'creator_name', default_value: campaign.title },
          { name: 'community_total_raised', default_value: `$${(campaign.current_amount ?? 0).toFixed(2)}` },
          { name: 'fam_reserve_pct', default_value: '5%' },
        ],
      }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.embedUrl) { setEmbedUrl(data.embedUrl); setStatus('iframe'); }
        else { setErrorMsg(data.error ?? 'No embed URL returned'); setStatus('error'); }
      })
      .catch(e => { setErrorMsg(e.message); setStatus('error'); });
  }, [campaign]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden flex flex-col" style={{ height: '85vh' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Step 3 — Co-Ownership Agreement</p>
            <p className="text-sm font-semibold text-gray-900 mt-0.5 truncate">{campaign.title}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 relative">
          {status === 'loading' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <FamSpinner size={56} />
            </div>
          )}
          {status === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-8 text-center">
              <XCircle className="w-10 h-10 text-red-400" />
              <p className="text-sm text-gray-600">{errorMsg || 'Failed to load signing session'}</p>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200 transition-colors">Close</button>
            </div>
          )}
          {status === 'iframe' && (
            <iframe src={embedUrl} className="absolute inset-0 w-full h-full border-0" title="Co-Ownership Agreement" />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDollars(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function truncateId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

type StatusType = 'active' | 'completed' | 'pending';

function StatusPill({ status }: { status: string }) {
  const normalized = status?.toLowerCase() as StatusType;
  const styles: Record<StatusType, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-blue-100 text-blue-700',
    pending: 'bg-amber-100 text-amber-700',
  };
  const cls = styles[normalized] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status ?? 'unknown'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// PIN Screen
// ---------------------------------------------------------------------------

function PinScreen({ onUnlock }: { onUnlock: () => void }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === 'fam2025') {
      localStorage.setItem('fam_admin_unlocked', '1');
      onUnlock();
    } else {
      setError('Incorrect PIN. Please try again.');
      setPin('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
        <div className="flex flex-col items-center gap-3 mb-6">
          <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center">
            <Lock className="w-7 h-7 text-indigo-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Admin Access</h1>
          <p className="text-sm text-gray-500 text-center">Enter the admin PIN to continue</p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="password"
            value={pin}
            onChange={(e) => { setPin(e.target.value); setError(''); }}
            placeholder="Enter PIN"
            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-400"
            autoFocus
          />
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <button
            type="submit"
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
          >
            Unlock
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

function StatCard({ label, value, icon: Icon, color }: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview Section
// ---------------------------------------------------------------------------

function OverviewSection() {
  const [stats, setStats] = useState<OverviewStats>({
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalRaised: 0,
    totalUsers: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentContribution[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const resp = await fetch('/api/admin?action=stats');
        const data = await resp.json();
        setStats({
          totalCampaigns: data.totalCampaigns ?? 0,
          activeCampaigns: data.activeCampaigns ?? 0,
          totalRaised: data.totalRaised ?? 0,
          totalUsers: data.totalUsers ?? 0,
        });
      } catch {
        setStats({ totalCampaigns: 0, activeCampaigns: 0, totalRaised: 0, totalUsers: 0 });
      }

      try {
        const resp = await fetch('/api/admin?action=recent-contributions');
        const rows = await resp.json();
        setRecentActivity(Array.isArray(rows) ? rows as RecentContribution[] : []);
      } catch {
        setRecentActivity([]);
      }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900">Overview</h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Campaigns"
          value={stats.totalCampaigns}
          icon={Megaphone}
          color="bg-indigo-100 text-indigo-600"
        />
        <StatCard
          label="Active Now"
          value={stats.activeCampaigns}
          icon={LayoutDashboard}
          color="bg-emerald-100 text-emerald-600"
        />
        <StatCard
          label="Total Raised"
          value={formatDollars(stats.totalRaised)}
          icon={DollarSign}
          color="bg-amber-100 text-amber-600"
        />
        <StatCard
          label="Total Users"
          value={stats.totalUsers}
          icon={Users}
          color="bg-blue-100 text-blue-600"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Recent Activity</h3>
        </div>
        {recentActivity.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">No recent contributions</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentActivity.map((item) => (
              <div key={item.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-gray-800">
                    {item.campaign_title ?? 'Unknown Campaign'}
                  </span>
                  <span className="text-xs text-gray-400">
                    {item.contribution_type === 'gift' ? 'Gift' : 'Investment'} · {formatDate(item.created_at)}
                  </span>
                </div>
                <span className="text-sm font-semibold text-indigo-600">{formatDollars(item.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campaigns Section
// ---------------------------------------------------------------------------

type CampaignStatus = 'all' | 'active' | 'completed' | 'pending';

function CampaignsSection() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CampaignStatus>('all');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [signingCampaign, setSigningCampaign] = useState<Campaign | null>(null);
  const [amortizationId, setAmortizationId] = useState<string | null>(null);
  const [scenarioData, setScenarioData] = useState<Record<string, {
    vehiclePrice: number;
    termMonths: number;
    monthlyPayment: number;
    creatorOwnershipPct: number;
    unitPrice: number;
  } | null>>({});
  const [scenarioLoading, setScenarioLoading] = useState<string | null>(null);
  const [profileChangesUserId, setProfileChangesUserId] = useState<string | null>(null);
  const [profileChangesData, setProfileChangesData] = useState<Record<string, Array<{
    field: string; old_value: string | null; new_value: string | null;
    is_material: boolean; reason: string | null; created_at: string;
  }> | null>>({});
  const [profileChangesLoading, setProfileChangesLoading] = useState<string | null>(null);

  const loadProfileChanges = async (userId: string) => {
    if (profileChangesUserId === userId) { setProfileChangesUserId(null); return; }
    if (profileChangesData[userId] !== undefined) { setProfileChangesUserId(userId); return; }
    setProfileChangesLoading(userId);
    try {
      const resp = await fetch(`/api/admin?action=profile_changes&userId=${encodeURIComponent(userId)}`);
      const data = await resp.json() as { changes?: typeof profileChangesData[string] };
      setProfileChangesData(prev => ({ ...prev, [userId]: data.changes ?? [] }));
      setProfileChangesUserId(userId);
    } catch {
      setProfileChangesData(prev => ({ ...prev, [userId]: [] }));
      setProfileChangesUserId(userId);
    }
    setProfileChangesLoading(null);
  };

  const loadScenario = async (campaignId: string) => {
    if (scenarioData[campaignId] !== undefined) {
      setAmortizationId(amortizationId === campaignId ? null : campaignId);
      return;
    }
    setScenarioLoading(campaignId);
    try {
      const resp = await fetch(`/api/financing?campaignId=${encodeURIComponent(campaignId)}`);
      const data = await resp.json();
      // financing API returns scenarios grouped by vehicle
      const scenarios = data.scenarios ?? data;
      let found = null;
      if (Array.isArray(scenarios)) {
        // Flat array or grouped — find first selected or recommended scenario
        for (const s of scenarios) {
          if (s.vehicles) {
            for (const v of s.vehicles) {
              if (v.status === 'selected' || v.is_recommended) {
                found = {
                  vehiclePrice: Number(v.unit_price ?? 0) * 1000,
                  termMonths: Number(v.term_months ?? 48),
                  monthlyPayment: Number(v.monthly_payment ?? 0),
                  creatorOwnershipPct: Number(v.creator_ownership_pct ?? 0),
                  unitPrice: Number(v.unit_price ?? 0),
                };
                break;
              }
            }
            if (found) break;
          } else if (s.status === 'selected' || s.is_recommended) {
            found = {
              vehiclePrice: Number(s.unit_price ?? 0) * 1000,
              termMonths: Number(s.term_months ?? 48),
              monthlyPayment: Number(s.monthly_payment ?? 0),
              creatorOwnershipPct: Number(s.creator_ownership_pct ?? 0),
              unitPrice: Number(s.unit_price ?? 0),
            };
            break;
          }
        }
        // Fallback to first scenario
        if (!found && scenarios.length > 0) {
          const first = scenarios[0].vehicles ? scenarios[0].vehicles[0] : scenarios[0];
          if (first) {
            found = {
              vehiclePrice: Number(first.unit_price ?? 0) * 1000,
              termMonths: Number(first.term_months ?? 48),
              monthlyPayment: Number(first.monthly_payment ?? 0),
              creatorOwnershipPct: Number(first.creator_ownership_pct ?? 0),
              unitPrice: Number(first.unit_price ?? 0),
            };
          }
        }
      }
      setScenarioData(prev => ({ ...prev, [campaignId]: found }));
      if (found) setAmortizationId(campaignId);
    } catch {
      setScenarioData(prev => ({ ...prev, [campaignId]: null }));
    }
    setScenarioLoading(null);
  };

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch('/api/admin?action=campaigns');
      const rows = await resp.json();
      setCampaigns(Array.isArray(rows) ? rows as Campaign[] : []);
    } catch {
      setCampaigns([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleStatusChange = async (id: string, newStatus: string) => {
    setUpdatingId(id);
    try {
      await fetch('/api/admin?action=campaign-status', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status: newStatus }),
      });
      setCampaigns((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status: newStatus } : c))
      );
    } catch (err) {
      console.error('Failed to update status', err);
    }
    setUpdatingId(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await fetch(`/api/admin?action=campaign&id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error('Failed to delete campaign', err);
    }
    setConfirmDeleteId(null);
  };

  const filtered = campaigns.filter((c) => {
    const matchesSearch = c.title?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const STATUS_CHIPS: { label: string; value: CampaignStatus }[] = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Completed', value: 'completed' },
    { label: 'Pending', value: 'pending' },
  ];

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold text-gray-900">Campaigns</h2>

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search campaigns..."
          className="flex-1 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
        <div className="flex gap-2">
          {STATUS_CHIPS.map((chip) => (
            <button
              key={chip.value}
              onClick={() => setStatusFilter(chip.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === chip.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">No campaigns found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Title</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Creator</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Raised / Goal</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Supporters</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Created</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((campaign) => (
                  <React.Fragment key={campaign.id}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[180px] truncate">
                      {campaign.title}
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                      {truncateId(campaign.user_id ?? '')}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={campaign.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {formatDollars(campaign.current_amount ?? 0)} / {formatDollars(campaign.goal_amount ?? 0)}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{campaign.supporter_count ?? 0}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(campaign.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <a
                          href={`/goal/${campaign.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                          title="View campaign"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>

                        <div className="relative">
                          <select
                            value={campaign.status}
                            disabled={updatingId === campaign.id}
                            onChange={(e) => handleStatusChange(campaign.id, e.target.value)}
                            className="appearance-none border border-gray-200 rounded-lg pl-2 pr-6 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white cursor-pointer disabled:opacity-50"
                          >
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                            <option value="pending">Pending</option>
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                        </div>

                        {['offer_sent', 'funded', 'processing_complete'].includes(campaign.status) && (
                          <button
                            onClick={() => setSigningCampaign(campaign)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                            title="Send Co-Ownership Agreement (Step 3)"
                          >
                            <FileSignature className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          onClick={() => loadProfileChanges(campaign.user_id)}
                          disabled={profileChangesLoading === campaign.user_id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            profileChangesUserId === campaign.user_id
                              ? 'text-amber-600 bg-amber-50'
                              : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                          }`}
                          title="View Profile Changes"
                        >
                          {profileChangesLoading === campaign.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <History className="w-4 h-4" />
                          )}
                        </button>

                        <button
                          onClick={() => loadScenario(campaign.id)}
                          disabled={scenarioLoading === campaign.id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            amortizationId === campaign.id
                              ? 'text-indigo-600 bg-indigo-50'
                              : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'
                          }`}
                          title="View Amortization"
                        >
                          {scenarioLoading === campaign.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <BarChart3 className="w-4 h-4" />
                          )}
                        </button>

                        {confirmDeleteId === campaign.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(campaign.id)}
                              className="px-2 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(campaign.id)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete campaign"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {amortizationId === campaign.id && scenarioData[campaign.id] && (
                    <tr>
                      <td colSpan={7} className="px-4 py-4 bg-gray-50 border-b border-gray-100">
                        <DualAmortizationView
                          vehiclePrice={scenarioData[campaign.id]!.vehiclePrice}
                          termMonths={scenarioData[campaign.id]!.termMonths}
                          monthlyPayment={scenarioData[campaign.id]!.monthlyPayment}
                          creatorOwnershipPct={scenarioData[campaign.id]!.creatorOwnershipPct}
                          unitPrice={scenarioData[campaign.id]!.unitPrice}
                        />
                      </td>
                    </tr>
                  )}
                  {amortizationId === campaign.id && scenarioData[campaign.id] === null && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 bg-gray-50 border-b border-gray-100 text-center">
                        <p className="text-sm text-gray-400">No financing scenarios available for this campaign</p>
                      </td>
                    </tr>
                  )}
                  {profileChangesUserId === campaign.user_id && (
                    <tr>
                      <td colSpan={7} className="px-4 py-4 bg-amber-50 border-b border-amber-100">
                        <p className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-2">Profile Change History</p>
                        {!profileChangesData[campaign.user_id] || profileChangesData[campaign.user_id]!.length === 0 ? (
                          <p className="text-xs text-gray-400">No profile changes recorded.</p>
                        ) : (
                          <div className="space-y-1.5 max-h-48 overflow-y-auto">
                            {profileChangesData[campaign.user_id]!.map((entry, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs">
                                <div className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${entry.is_material ? 'bg-amber-500' : 'bg-gray-300'}`} />
                                <div>
                                  <span className="font-semibold text-gray-700">{entry.field}</span>
                                  {entry.is_material && <span className="ml-1 text-[10px] font-bold bg-amber-200 text-amber-800 px-1 rounded">Material</span>}
                                  <span className="text-gray-400 ml-1">
                                    <span className="line-through">{entry.old_value ?? '(none)'}</span>
                                    {' → '}
                                    <span className="text-gray-600">{entry.new_value ?? '(none)'}</span>
                                  </span>
                                  {entry.reason && <span className="text-gray-400 ml-1 italic">"{entry.reason}"</span>}
                                  <span className="text-gray-300 ml-2">{new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {signingCampaign && (
        <CoOwnershipSigningModal
          campaign={signingCampaign}
          onClose={() => setSigningCampaign(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users Section
// ---------------------------------------------------------------------------

function UsersSection() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch('/api/admin?action=users');
      const rows = await resp.json();
      setUsers(Array.isArray(rows) ? rows as UserProfile[] : []);
    } catch {
      setUsers([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleDelete = async (id: string) => {
    try {
      await fetch(`/api/admin?action=user&id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      console.error('Failed to delete user', err);
    }
    setConfirmDeleteId(null);
  };

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold text-gray-900">Users</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">No users found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">ID</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Email</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Created At</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {truncateId(user.id)}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{user.name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{user.email ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(user.created_at)}</td>
                    <td className="px-4 py-3">
                      {confirmDeleteId === user.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="px-2 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(user.id)}
                          className="flex items-center gap-1.5 px-2 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Financials Section
// ---------------------------------------------------------------------------

function FinancialsSection() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const resp = await fetch('/api/admin?action=transactions');
        const rows = await resp.json();
        setTransactions(Array.isArray(rows) ? rows as Transaction[] : []);
      } catch {
        setTransactions([]);
      }
      setLoading(false);
    }
    load();
  }, []);

  const totalGifts = transactions
    .filter((t) => t.contribution_type === 'gift')
    .reduce((sum, t) => sum + (t.amount ?? 0), 0);

  const totalInvestments = transactions
    .filter((t) => t.contribution_type === 'investment')
    .reduce((sum, t) => sum + (t.amount ?? 0), 0);

  const totalRaised = totalGifts + totalInvestments;

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold text-gray-900">Financials</h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Total Gifts"
          value={formatDollars(totalGifts)}
          icon={DollarSign}
          color="bg-emerald-100 text-emerald-600"
        />
        <StatCard
          label="Total Investments"
          value={formatDollars(totalInvestments)}
          icon={DollarSign}
          color="bg-blue-100 text-blue-600"
        />
        <StatCard
          label="Total Raised"
          value={formatDollars(totalRaised)}
          icon={DollarSign}
          color="bg-indigo-100 text-indigo-600"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Transaction Log</h3>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">No transactions found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Contribution ID</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Campaign</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Amount</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Created At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {truncateId(tx.id)}
                    </td>
                    <td className="px-4 py-3 text-gray-800 max-w-[200px] truncate">
                      {tx.campaign_title ?? '—'}
                    </td>
                    <td className="px-4 py-3 font-semibold text-indigo-600">
                      {formatDollars(tx.amount ?? 0)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          tx.contribution_type === 'gift'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {tx.contribution_type === 'gift' ? 'Gift' : 'Investment'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(tx.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dealers Section
// ---------------------------------------------------------------------------

interface DealerRecord {
  id: string;
  name: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  city: string;
  state: string;
  isVerified: boolean;
  isBoosted: boolean;
  vehicleCount: number;
  addedAt: string;
}

interface ScrapedVehicle {
  vin?: string | null;
  year?: number | null;
  make?: string;
  model?: string;
  trim?: string;
  price?: number | null;
  mileage?: number | null;
  location?: string;
  nhtsa_verified?: boolean;
}

interface ScrapeResult {
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  address: string;
  description: string;
  logo: string;
  website: string;
  vehicles: ScrapedVehicle[];
  vinCount: number;
  inventoryPagesChecked: number;
}

type DealerTier = 'free' | 'boost';

// ── Scrape preview card ──────────────────────────────────────────────────────
function ScrapePreviewCard({ preview }: { preview: ScrapeResult }) {
  return (
    <div className="border border-indigo-100 rounded-xl overflow-hidden bg-indigo-50/40">
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-indigo-100">
        <img src={preview.logo} alt="logo"
          className="w-10 h-10 rounded-lg object-contain bg-gray-100 border border-gray-200 p-1"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-sm truncate">{preview.name || '(Name not found)'}</p>
          <p className="text-xs text-gray-500">{[preview.city, preview.state].filter(Boolean).join(', ') || 'Location unknown'}</p>
        </div>
        {preview.website && (
          <a href={preview.website} target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:text-indigo-700">
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
      <div className="px-4 py-3">
        <p className="text-xs font-semibold text-gray-700 mb-2">
          {preview.vehicles.length > 0
            ? `${preview.vehicles.length} vehicles · ${preview.vinCount} VINs · ${preview.inventoryPagesChecked} page${preview.inventoryPagesChecked !== 1 ? 's' : ''} scanned`
            : preview.vinCount > 0 ? `${preview.vinCount} VINs found — could not decode` : 'No vehicles found'}
        </p>
        {preview.vehicles.length > 0 && (
          <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
            {preview.vehicles.slice(0, 30).map((v, i) => (
              <div key={v.vin ?? i} className="flex items-center justify-between text-xs py-1 border-b border-indigo-100 last:border-0">
                <span className="text-gray-700 font-medium">{v.year} {v.make} {v.model}{v.trim ? ` ${v.trim}` : ''}</span>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  {v.price != null && <span className="text-emerald-600 font-semibold">${v.price.toLocaleString()}</span>}
                  {v.mileage != null && <span className="text-gray-400">{v.mileage.toLocaleString()}mi</span>}
                  {v.vin && <span className="text-gray-400 font-mono">{v.vin.slice(-6)}</span>}
                  {v.nhtsa_verified && <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />}
                </div>
              </div>
            ))}
            {preview.vehicles.length > 30 && (
              <p className="text-xs text-center text-indigo-400 pt-1">+{preview.vehicles.length - 30} more</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tier selector ─────────────────────────────────────────────────────────────
function TierSelector({ tier, onChange }: { tier: DealerTier; onChange: (t: DealerTier) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {([
        { id: 'free' as const, label: 'Free', price: '$0/mo', desc: 'Full listing, standing offers, QR toolkit' },
        { id: 'boost' as const, label: 'Inventory Boost', price: '$149/mo', desc: 'Top placement + featured dealer badge' },
      ]).map(({ id, label, price, desc }) => (
        <button key={id} type="button" onClick={() => onChange(id)}
          className={`text-left p-3 rounded-xl border-2 transition-all ${tier === id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-200'}`}>
          <p className="text-xs font-bold text-gray-900">{label}</p>
          <p className="text-sm font-extrabold text-indigo-600">{price}</p>
          <p className="text-xs text-gray-400 mt-0.5 leading-tight">{desc}</p>
        </button>
      ))}
    </div>
  );
}

// ── Dealer details form ───────────────────────────────────────────────────────
function DealerForm({ name, email, phone, city, state, website,
  onName, onEmail, onPhone, onCity, onState, onWebsite, showWebsite = false }: {
  name: string; email: string; phone: string; city: string; state: string; website?: string;
  onName: (v: string) => void; onEmail: (v: string) => void; onPhone: (v: string) => void;
  onCity: (v: string) => void; onState: (v: string) => void; onWebsite?: (v: string) => void;
  showWebsite?: boolean;
}) {
  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300';
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Business Name *</label>
        <input type="text" value={name} onChange={(e) => onName(e.target.value)} placeholder="Metro Auto Group" className={inp} />
      </div>
      {showWebsite && onWebsite && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
          <input type="text" value={website ?? ''} onChange={(e) => onWebsite(e.target.value)} placeholder="https://dealersite.com" className={inp} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Contact Email</label>
          <input type="email" value={email} onChange={(e) => onEmail(e.target.value)} placeholder="info@dealer.com" className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
          <input type="tel" value={phone} onChange={(e) => onPhone(e.target.value)} placeholder="(555) 555-5555" className={inp} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
          <input type="text" value={city} onChange={(e) => onCity(e.target.value)} placeholder="Atlanta" className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
          <input type="text" value={state} onChange={(e) => onState(e.target.value.toUpperCase().slice(0, 2))}
            placeholder="GA" maxLength={2} className={inp + ' uppercase'} />
        </div>
      </div>
    </div>
  );
}

// ── Zip code search result type ───────────────────────────────────────────────
interface ZipDealerResult {
  place_id: string;
  name: string;
  address: string;
  phone: string;
  website: string;
  scanState: 'idle' | 'scanning' | 'done' | 'error';
  scrapeResult?: ScrapeResult;
  scanError?: string;
  saving?: boolean;
  saved?: boolean;
}

// ── AddDealerSlideOut ─────────────────────────────────────────────────────────
function AddDealerSlideOut({ isOpen, onClose, onAdded }: {
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  type Mode = 'zip' | 'url' | 'manual';
  const [mode, setMode] = useState<Mode>('zip');

  // Zip search state
  const [zipCode, setZipCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [zipResults, setZipResults] = useState<ZipDealerResult[]>([]);

  // URL scan state
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState('');
  const [preview, setPreview] = useState<ScrapeResult | null>(null);
  const [tier, setTier] = useState<DealerTier>('free');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Manual state
  const [mName, setMName] = useState('');
  const [mEmail, setMEmail] = useState('');
  const [mPhone, setMPhone] = useState('');
  const [mCity, setMCity] = useState('');
  const [mState, setMState] = useState('');
  const [mWebsite, setMWebsite] = useState('');
  const [mTier, setMTier] = useState<DealerTier>('free');
  const [mSaving, setMSaving] = useState(false);
  const [mSaved, setMSaved] = useState(false);

  // URL scan form state (pre-filled from scrape)
  const [fName, setFName] = useState('');
  const [fEmail, setFEmail] = useState('');
  const [fPhone, setFPhone] = useState('');
  const [fCity, setFCity] = useState('');
  const [fState, setFState] = useState('');

  const reset = () => {
    setZipCode(''); setSearching(false); setSearchError(''); setZipResults([]);
    setScrapeUrl(''); setScraping(false); setScrapeError(''); setPreview(null);
    setTier('free'); setSaving(false); setSaved(false);
    setFName(''); setFEmail(''); setFPhone(''); setFCity(''); setFState('');
    setMName(''); setMEmail(''); setMPhone(''); setMCity(''); setMState(''); setMWebsite('');
    setMTier('free'); setMSaving(false); setMSaved(false);
  };

  const handleClose = () => { reset(); onClose(); };

  // ── Zip search ──────────────────────────────────────────────────────────────
  const handleZipSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!zipCode.trim()) return;
    setSearching(true); setSearchError(''); setZipResults([]);
    try {
      const res = await fetch(`/api/search-dealers-by-zip?zip=${encodeURIComponent(zipCode.trim())}`);
      const data = await res.json() as { dealers?: ZipDealerResult[]; error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Search failed');
      setZipResults((data.dealers ?? []).map((d) => ({ ...d, scanState: 'idle' as const })));
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    }
    setSearching(false);
  };

  const handleScanDealer = async (place_id: string) => {
    const dealer = zipResults.find((d) => d.place_id === place_id);
    if (!dealer?.website) return;
    setZipResults((prev) => prev.map((d) => d.place_id === place_id ? { ...d, scanState: 'scanning' as const, scanError: undefined } : d));
    try {
      const res = await fetch('/api/scrape-dealer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: dealer.website }),
      });
      const data = await res.json() as ScrapeResult & { error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Scrape failed');
      setZipResults((prev) => prev.map((d) => d.place_id === place_id
        ? { ...d, scanState: 'done' as const, scrapeResult: { ...data, vehicles: data.vehicles ?? [] } }
        : d));
    } catch (err) {
      setZipResults((prev) => prev.map((d) => d.place_id === place_id
        ? { ...d, scanState: 'error' as const, scanError: err instanceof Error ? err.message : 'Scrape failed' }
        : d));
    }
  };

  const handleSaveZipDealer = async (place_id: string) => {
    const dealer = zipResults.find((d) => d.place_id === place_id);
    if (!dealer) return;
    setZipResults((prev) => prev.map((d) => d.place_id === place_id ? { ...d, saving: true } : d));
    try {
      const sr = dealer.scrapeResult;
      const res = await fetch('/api/save-dealer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealer: {
            name: sr?.name || dealer.name,
            website: dealer.website || sr?.website,
            email: sr?.email || '',
            phone: sr?.phone || dealer.phone,
            address: sr?.address || dealer.address,
            city: sr?.city || '',
            state: sr?.state || '',
            logo: sr?.logo || '',
          },
          vehicles: sr?.vehicles ?? [],
          tier: 'free',
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setZipResults((prev) => prev.map((d) => d.place_id === place_id ? { ...d, saving: false, saved: true } : d));
      onAdded();
    } catch {
      setZipResults((prev) => prev.map((d) => d.place_id === place_id ? { ...d, saving: false } : d));
    }
  };

  // ── URL scan ────────────────────────────────────────────────────────────────
  const handleScrape = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scrapeUrl) return;
    setScraping(true); setScrapeError(''); setPreview(null);
    try {
      const res = await fetch('/api/scrape-dealer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: scrapeUrl }),
      });
      const data = await res.json() as ScrapeResult & { error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Scrape failed');
      setPreview({ ...data, vehicles: data.vehicles ?? [] });
      setFName(data.name ?? ''); setFEmail(data.email ?? ''); setFPhone(data.phone ?? '');
      setFCity(data.city ?? ''); setFState(data.state ?? '');
    } catch (err) {
      setScrapeError(err instanceof Error ? err.message : 'Scrape failed');
    }
    setScraping(false);
  };

  const handleSaveUrl = async () => {
    if (!fName) return;
    setSaving(true);
    try {
      const res = await fetch('/api/save-dealer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealer: {
            name: fName, website: preview?.website ?? scrapeUrl,
            email: fEmail, phone: fPhone, city: fCity, state: fState,
            address: preview?.address ?? '', logo: preview?.logo ?? '',
          },
          vehicles: preview?.vehicles ?? [],
          tier,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setSaved(true);
      onAdded();
    } catch { /* show inline error if needed */ }
    setSaving(false);
  };

  // ── Manual save ─────────────────────────────────────────────────────────────
  const handleSaveManual = async () => {
    if (!mName) return;
    setMSaving(true);
    try {
      const res = await fetch('/api/save-dealer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealer: { name: mName, website: mWebsite, email: mEmail, phone: mPhone, city: mCity, state: mState },
          vehicles: [],
          tier: mTier,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setMSaved(true);
      onAdded();
    } catch { /* show inline error */ }
    setMSaving(false);
  };

  const TABS: { id: Mode; label: string; icon: React.ElementType }[] = [
    { id: 'zip', label: 'Search by Zip', icon: MapPin },
    { id: 'url', label: 'Scan URL', icon: Globe },
    { id: 'manual', label: 'Manual', icon: Store },
  ];

  return (
    <>
      <div className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={handleClose} />
      <div className={`fixed top-0 right-0 h-full w-full sm:w-[520px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-bold text-gray-900">Add Dealer to Network</h2>
          <button onClick={handleClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b border-gray-100 shrink-0">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setMode(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold transition-colors border-b-2 ${
                mode === id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          {/* ── ZIP SEARCH ─────────────────────────────────────────────────── */}
          {mode === 'zip' && (
            <>
              <div>
                <p className="text-xs text-gray-400 mb-3">
                  Search Google Places for used car dealers near a zip code. Scan each dealer's website with Browserbase to pull inventory.
                </p>
                <form onSubmit={handleZipSearch} className="flex gap-2">
                  <input type="text" value={zipCode} onChange={(e) => setZipCode(e.target.value)}
                    placeholder="Enter zip code e.g. 30318"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                  <button type="submit" disabled={!zipCode.trim() || searching}
                    className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-50 shrink-0">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    {searching ? 'Searching…' : 'Search'}
                  </button>
                </form>
                {searchError && <p className="mt-2 text-xs text-red-500">{searchError}</p>}
              </div>

              {zipResults.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-600">{zipResults.length} dealers found</p>
                  {zipResults.map((dealer) => (
                    <div key={dealer.place_id} className="border border-gray-200 rounded-xl overflow-hidden">
                      {/* Dealer header */}
                      <div className="px-4 py-3 bg-gray-50 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{dealer.name}</p>
                          {dealer.address && <p className="text-xs text-gray-400 mt-0.5 truncate">{dealer.address}</p>}
                          <div className="flex items-center gap-3 mt-1">
                            {dealer.phone && <span className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{dealer.phone}</span>}
                            {dealer.website && (
                              <a href={dealer.website} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-indigo-500 hover:underline flex items-center gap-1 truncate max-w-[160px]">
                                <Globe className="w-3 h-3 shrink-0" />
                                {dealer.website.replace(/^https?:\/\//, '')}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          {dealer.saved ? (
                            <span className="flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                              <CheckCircle className="w-3.5 h-3.5" /> Added
                            </span>
                          ) : (
                            <>
                              {dealer.website && dealer.scanState === 'idle' && (
                                <button onClick={() => handleScanDealer(dealer.place_id)}
                                  className="text-xs px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-lg font-semibold hover:bg-indigo-200 transition-colors">
                                  Scan Inventory
                                </button>
                              )}
                              {dealer.scanState === 'scanning' && (
                                <span className="flex items-center gap-1 text-xs text-indigo-500">
                                  <Loader2 className="w-3 h-3 animate-spin" /> Scanning…
                                </span>
                              )}
                              {(dealer.scanState === 'done' || dealer.scanState === 'error') && (
                                <button onClick={() => handleSaveZipDealer(dealer.place_id)}
                                  disabled={dealer.saving}
                                  className="text-xs px-2.5 py-1 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                                  {dealer.saving ? 'Saving…' : 'Add to Network'}
                                </button>
                              )}
                              {!dealer.website && (
                                <button onClick={() => handleSaveZipDealer(dealer.place_id)}
                                  disabled={dealer.saving}
                                  className="text-xs px-2.5 py-1 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 disabled:opacity-50 transition-colors">
                                  {dealer.saving ? 'Saving…' : 'Add (no site)'}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Scan result */}
                      {dealer.scanState === 'done' && dealer.scrapeResult && (
                        <div className="px-4 py-2 border-t border-gray-100">
                          <p className="text-xs text-emerald-700 font-medium">
                            {dealer.scrapeResult.vehicles.length} vehicles · {dealer.scrapeResult.vinCount} VINs verified
                          </p>
                          {dealer.scrapeResult.vehicles.slice(0, 5).map((v, i) => (
                            <div key={i} className="text-xs text-gray-500 mt-0.5">
                              {v.year} {v.make} {v.model}{v.price != null ? ` — $${v.price.toLocaleString()}` : ''}
                              {v.mileage != null ? ` · ${v.mileage.toLocaleString()}mi` : ''}
                            </div>
                          ))}
                          {dealer.scrapeResult.vehicles.length > 5 && (
                            <p className="text-xs text-indigo-400 mt-0.5">+{dealer.scrapeResult.vehicles.length - 5} more</p>
                          )}
                        </div>
                      )}
                      {dealer.scanState === 'error' && (
                        <div className="px-4 py-2 border-t border-gray-100">
                          <p className="text-xs text-amber-600">{dealer.scanError ?? 'Scan failed'} — will save without inventory.</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── URL SCAN ───────────────────────────────────────────────────── */}
          {mode === 'url' && (
            <>
              <div>
                <p className="text-xs text-gray-400 mb-3">
                  Enter a dealer website URL. Browserbase + Stagehand will scrape inventory, verify VINs via NHTSA, and pre-fill the form below.
                </p>
                <form onSubmit={handleScrape} className="flex gap-2">
                  <input type="text" value={scrapeUrl}
                    onChange={(e) => { setScrapeUrl(e.target.value); setPreview(null); setScrapeError(''); }}
                    placeholder="https://dealerwebsite.com"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                  <button type="submit" disabled={!scrapeUrl || scraping}
                    className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-50 shrink-0">
                    {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                    {scraping ? 'Scanning…' : 'Scan Site'}
                  </button>
                </form>
                {scrapeError && <p className="mt-2 text-xs text-red-500">{scrapeError}</p>}
              </div>

              {preview && <ScrapePreviewCard preview={preview} />}

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-800">{preview ? 'Confirm details' : 'Or enter manually'}</h3>
                <DealerForm name={fName} email={fEmail} phone={fPhone} city={fCity} state={fState}
                  onName={setFName} onEmail={setFEmail} onPhone={setFPhone} onCity={setFCity} onState={setFState} />
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Platform tier</h3>
                <TierSelector tier={tier} onChange={setTier} />
              </div>

              <div className="space-y-2 pb-4">
                {saved ? (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                    <p className="text-sm font-semibold text-emerald-800">Dealer saved to network!</p>
                  </div>
                ) : (
                  <button onClick={handleSaveUrl} disabled={!fName || saving}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                    {saving ? 'Saving…' : 'Save to Network'}
                  </button>
                )}
              </div>
            </>
          )}

          {/* ── MANUAL ─────────────────────────────────────────────────────── */}
          {mode === 'manual' && (
            <>
              <p className="text-xs text-gray-400">Enter dealer details manually. You can add inventory separately from the Inventory tab.</p>
              <DealerForm name={mName} email={mEmail} phone={mPhone} city={mCity} state={mState} website={mWebsite}
                onName={setMName} onEmail={setMEmail} onPhone={setMPhone} onCity={setMCity} onState={setMState} onWebsite={setMWebsite}
                showWebsite />

              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Platform tier</h3>
                <TierSelector tier={mTier} onChange={setMTier} />
              </div>

              <div className="pb-4">
                {mSaved ? (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                    <p className="text-sm font-semibold text-emerald-800">Dealer created!</p>
                  </div>
                ) : (
                  <button onClick={handleSaveManual} disabled={!mName || mSaving}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                    {mSaving ? 'Saving…' : 'Create Dealer Account'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── DealersSection ────────────────────────────────────────────────────────────
function DealersSection() {
  const [dealers, setDealers] = useState<DealerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [slideOutOpen, setSlideOutOpen] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadDealers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch('/api/admin?action=dealers');
      const rows = await resp.json();
      setDealers(Array.isArray(rows) ? rows as DealerRecord[] : []);
    } catch {
      setDealers([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadDealers(); }, [loadDealers]);

  const handleToggleVerified = async (id: string) => {
    setTogglingId(id);
    const dealer = dealers.find((d) => d.id === id);
    if (!dealer) { setTogglingId(null); return; }
    const newVal = !dealer.isVerified;
    setDealers((prev) => prev.map((d) => d.id === id ? { ...d, isVerified: newVal } : d));
    try {
      await fetch(`/api/admin?action=dealer-verify&id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verified: newVal }),
      });
    } catch { /* no-op */ }
    setTogglingId(null);
  };

  const handleToggleBoosted = async (id: string) => {
    setTogglingId(id);
    const dealer = dealers.find((d) => d.id === id);
    if (!dealer) { setTogglingId(null); return; }
    const newVal = !dealer.isBoosted;
    setDealers((prev) => prev.map((d) => d.id === id ? { ...d, isBoosted: newVal } : d));
    try {
      await fetch(`/api/admin?action=dealer-boost&id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ boosted: newVal }),
      });
    } catch { /* no-op */ }
    setTogglingId(null);
  };

  const handleRemove = async (id: string) => {
    setDealers((prev) => prev.filter((d) => d.id !== id));
    setConfirmRemoveId(null);
    try {
      await fetch(`/api/admin?action=dealer&id=${encodeURIComponent(id)}`, { method: 'DELETE' });
    } catch { /* no-op */ }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Dealers <span className="text-sm font-normal text-gray-400 ml-1">{dealers.length} in network</span></h2>
        <button onClick={() => setSlideOutOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity">
          <Store className="w-4 h-4" />
          Add Dealer
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
          </div>
        ) : dealers.length === 0 ? (
          <div className="text-center py-12">
            <Store className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No dealers yet. Search by zip code to find and add dealers.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Location</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Vehicles</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Verified</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Boosted</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Added</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dealers.map((dealer) => (
                  <tr key={dealer.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{dealer.name}</p>
                      {dealer.website && (
                        <a href={dealer.website} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-indigo-500 hover:underline flex items-center gap-0.5 mt-0.5">
                          <Globe className="w-3 h-3" />
                          {dealer.website.replace(/^https?:\/\//, '').slice(0, 30)}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {[dealer.city, dealer.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3">
                      {dealer.contactEmail && <p className="text-xs text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" />{dealer.contactEmail}</p>}
                      {dealer.contactPhone && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{dealer.contactPhone}</p>}
                      {!dealer.contactEmail && !dealer.contactPhone && <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-700 font-medium">{dealer.vehicleCount}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleToggleVerified(dealer.id)} disabled={togglingId === dealer.id}
                        className="flex items-center gap-1 disabled:opacity-50">
                        {dealer.isVerified
                          ? <CheckCircle className="w-5 h-5 text-emerald-500" />
                          : <XCircle className="w-5 h-5 text-gray-300" />}
                        <span className={`text-xs font-medium ${dealer.isVerified ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {dealer.isVerified ? 'Yes' : 'No'}
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleToggleBoosted(dealer.id)} disabled={togglingId === dealer.id}
                        className="flex items-center gap-1 disabled:opacity-50">
                        <Zap className={`w-5 h-5 ${dealer.isBoosted ? 'text-amber-500 fill-amber-400' : 'text-gray-300'}`} />
                        <span className={`text-xs font-medium ${dealer.isBoosted ? 'text-amber-600' : 'text-gray-400'}`}>
                          {dealer.isBoosted ? 'Yes' : 'No'}
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(dealer.addedAt)}</td>
                    <td className="px-4 py-3">
                      {confirmRemoveId === dealer.id ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleRemove(dealer.id)}
                            className="px-2 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700">Yes</button>
                          <button onClick={() => setConfirmRemoveId(null)}
                            className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300">No</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmRemoveId(dealer.id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddDealerSlideOut
        isOpen={slideOutOpen}
        onClose={() => setSlideOutOpen(false)}
        onAdded={() => { setSlideOutOpen(false); loadDealers(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inventory Section — single VIN + CSV import
// ---------------------------------------------------------------------------

interface ManualVehicle extends NHTSAVehicle {
  price: number;
  mileage: number;
  dealerId: string;
  addedAt: string;
}

function InventorySection() {
  type Tab = 'single' | 'csv';
  const [tab, setTab] = useState<Tab>('single');

  // Shared: dealers list for selector
  const [dealers, setDealers] = useState<{ id: string; name: string }[]>([]);
  const [selectedDealer, setSelectedDealer] = useState('');

  useEffect(() => {
    fetch('/api/admin?action=dealer-list')
      .then((r) => r.json())
      .then((rows: { id: string; name: string }[]) => {
        if (Array.isArray(rows)) {
          setDealers(rows);
          if (rows.length > 0 && !selectedDealer) setSelectedDealer(String(rows[0].id));
        }
      })
      .catch(() => {});
  }, []);

  // ── Single VIN state ────────────────────────────────────────────────────────
  const [vin, setVin] = useState('');
  const [decoding, setDecoding] = useState(false);
  const [decoded, setDecoded] = useState<NHTSAVehicle | null>(null);
  const [decodeError, setDecodeError] = useState('');
  const [price, setPrice] = useState('');
  const [mileage, setMileage] = useState('');
  const [vinSaving, setVinSaving] = useState(false);
  const [vinSaved, setVinSaved] = useState(false);
  const [sessionVehicles, setSessionVehicles] = useState<ManualVehicle[]>([]);

  const handleDecode = async () => {
    const v = vin.trim().toUpperCase();
    if (v.length !== 17) { setDecodeError('VIN must be exactly 17 characters.'); return; }
    setDecoding(true); setDecodeError(''); setDecoded(null);
    const result = await decodeVin(v);
    setDecoding(false);
    if (!result) setDecodeError('NHTSA could not decode this VIN.');
    else setDecoded(result);
  };

  const handleSaveVin = async () => {
    if (!decoded || !selectedDealer) return;
    setVinSaving(true);
    try {
      const res = await fetch('/api/save-dealer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealer: { name: dealers.find((d) => d.id === selectedDealer)?.name ?? '' },
          vehicles: [{
            vin: decoded.vin, year: decoded.year, make: decoded.make, model: decoded.model,
            trim: decoded.trim, price: parseFloat(price) || null, mileage: parseInt(mileage, 10) || null,
          }],
          tier: 'free',
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const vehicle: ManualVehicle = { ...decoded, price: parseFloat(price) || 0, mileage: parseInt(mileage, 10) || 0, dealerId: selectedDealer, addedAt: new Date().toISOString() };
      setSessionVehicles((prev) => [vehicle, ...prev]);
      setVin(''); setDecoded(null); setPrice(''); setMileage('');
      setVinSaved(true); setTimeout(() => setVinSaved(false), 2500);
    } catch { /* silent */ }
    setVinSaving(false);
  };

  // ── CSV state ───────────────────────────────────────────────────────────────
  const fileRef = useRef<HTMLInputElement>(null);
  const [csvText, setCsvText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ inserted: number; skipped: number; total: number } | null>(null);
  const [importError, setImportError] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText((ev.target?.result as string) ?? '');
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvText.trim() || !selectedDealer) return;
    setImporting(true); setImportResult(null); setImportError('');
    try {
      const res = await fetch('/api/import-inventory-csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealerId: selectedDealer, csv: csvText }),
      });
      const data = await res.json() as { inserted: number; skipped: number; total: number; error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Import failed');
      setImportResult(data);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    }
    setImporting(false);
  };

  const handleDownloadTemplate = () => {
    const csv = 'vin,year,make,model,trim,price,mileage,location\n1HGCM82633A004352,2003,Honda,Accord,EX,12500,87000,Atlanta GA\n1FTFW1ET5DFC10312,2013,Ford,F-150,XLT,22000,61000,Dallas TX';
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = 'fam-inventory-template.csv';
    a.click();
  };

  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300';

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900">Inventory</h2>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {([{ id: 'single', label: 'Single VIN' }, { id: 'csv', label: 'CSV Import' }] as const).map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-5 py-2.5 text-sm font-semibold transition-colors border-b-2 ${
              tab === id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Dealer selector (shared) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Dealer</label>
        {dealers.length === 0 ? (
          <p className="text-xs text-gray-400">No dealers yet — add one first from the Dealers tab.</p>
        ) : (
          <select value={selectedDealer} onChange={(e) => setSelectedDealer(e.target.value)} className={inp}>
            {dealers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
      </div>

      {/* ── SINGLE VIN TAB ─────────────────────────────────────────────────── */}
      {tab === 'single' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
          <p className="text-sm font-semibold text-gray-700">Decode & add a single VIN</p>
          <div className="flex gap-2">
            <input type="text" value={vin}
              onChange={(e) => { setVin(e.target.value.toUpperCase()); setDecodeError(''); setDecoded(null); }}
              placeholder="Enter 17-character VIN" maxLength={17}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-indigo-400" />
            <button onClick={handleDecode} disabled={decoding || vin.length < 17}
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-all">
              {decoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Decode
            </button>
          </div>
          {decodeError && <p className="text-xs text-red-500">{decodeError}</p>}

          {decoded && (
            <div className="border border-indigo-100 bg-indigo-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-indigo-600" />
                <p className="text-sm font-semibold text-indigo-800">
                  {decoded.year} {decoded.make} {decoded.model}{decoded.trim ? ` ${decoded.trim}` : ''}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                {decoded.bodyStyle && <span>Body: {decoded.bodyStyle}</span>}
                {decoded.driveType && <span>Drive: {decoded.driveType}</span>}
                {decoded.fuelType && <span>Fuel: {decoded.fuelType}</span>}
                {decoded.engineCylinders && <span>Cylinders: {decoded.engineCylinders}</span>}
                {decoded.displacementL && <span>Displacement: {decoded.displacementL}L</span>}
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-indigo-100">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Price ($)</label>
                  <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="18500"
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Mileage</label>
                  <input type="number" value={mileage} onChange={(e) => setMileage(e.target.value)} placeholder="42000"
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                </div>
              </div>
              <button onClick={handleSaveVin} disabled={!selectedDealer || vinSaving}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-all">
                {vinSaving ? 'Saving…' : 'Save to Inventory'}
              </button>
              {vinSaved && <p className="text-xs text-center text-emerald-600 font-medium">Saved!</p>}
            </div>
          )}
        </div>
      )}

      {/* ── CSV IMPORT TAB ─────────────────────────────────────────────────── */}
      {tab === 'csv' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">Bulk import from CSV</p>
            <button onClick={handleDownloadTemplate}
              className="flex items-center gap-1.5 text-xs text-indigo-600 font-semibold hover:text-indigo-800 transition-colors">
              <FileDown className="w-4 h-4" />
              Download Template
            </button>
          </div>
          <p className="text-xs text-gray-400">
            Required columns: <code className="bg-gray-100 px-1 py-0.5 rounded">vin, year, make, model</code>. Optional: trim, price, mileage, location. VINs are verified via NHTSA.
          </p>

          <div className="flex gap-2">
            <button onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
              <Upload className="w-4 h-4" />
              Choose CSV file
            </button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileChange} />
            {csvText && <span className="text-xs text-emerald-600 self-center font-medium">File loaded ✓</span>}
          </div>

          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={'vin,year,make,model,trim,price,mileage,location\n1HGCM82633A004352,2003,Honda,Accord,EX,12500,87000,Atlanta GA'}
            rows={6}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-y"
          />

          <button onClick={handleImport} disabled={!csvText.trim() || !selectedDealer || importing}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-all">
            {importing ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Importing & verifying VINs…</span> : 'Import Inventory'}
          </button>

          {importResult && (
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">Import complete</p>
                <p className="text-xs text-emerald-600">
                  {importResult.inserted} inserted · {importResult.skipped} skipped · {importResult.total} total rows
                </p>
              </div>
            </div>
          )}
          {importError && <p className="text-xs text-red-500">{importError}</p>}
        </div>
      )}

      {/* Session vehicles */}
      {sessionVehicles.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-700">{sessionVehicles.length} vehicle{sessionVehicles.length !== 1 ? 's' : ''} added this session</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Vehicle</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">VIN</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Price</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Mileage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sessionVehicles.map((v) => (
                <tr key={v.vin} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900">{v.year} {v.make} {v.model}{v.trim ? ` ${v.trim}` : ''}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{v.vin}</td>
                  <td className="px-4 py-2.5 text-gray-700">{v.price ? `$${v.price.toLocaleString()}` : '—'}</td>
                  <td className="px-4 py-2.5 text-gray-700">{v.mileage ? v.mileage.toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Advisor Section
// ---------------------------------------------------------------------------

interface PendingApproval {
  id: string;
  title: string;
  user_id: string;
  created_at: string;
  fam_score?: number;
  down_payment_amount?: number;
  plaid_verified?: boolean;
  doc_verified?: boolean;
  address_verified?: boolean;
  social_accounts_count?: number;
}

interface AdvisorCampaign {
  id: string;
  title: string;
  user_id: string;
  status: string;
  current_amount: number;
  goal_amount: number;
  supporter_count: number;
  created_at: string;
}

interface AdvisorRecord {
  id: string;
  clerk_user_id: string;
  name: string;
  email: string;
  title: string;
  campaign_count: number;
  active: boolean;
}

function AdvisorSection() {
  // ── State ──
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [advisorCampaigns, setAdvisorCampaigns] = useState<AdvisorCampaign[]>([]);
  const [advisors, setAdvisors] = useState<AdvisorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Request info modal
  const [requestInfoId, setRequestInfoId] = useState<string | null>(null);
  const [requestInfoMsg, setRequestInfoMsg] = useState('');

  // Coaching dropdown
  const [coachingCampaignId, setCoachingCampaignId] = useState<string | null>(null);
  const [coachingSending, setCoachingSending] = useState(false);

  // Add advisor form
  const [showAddAdvisor, setShowAddAdvisor] = useState(false);
  const [newAdvisor, setNewAdvisor] = useState({ clerkUserId: '', name: '', email: '', title: '' });
  const [addingAdvisor, setAddingAdvisor] = useState(false);

  // ── Load data ──
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [approvalsResp, campaignsResp, advisorsResp] = await Promise.all([
          fetch('/api/admin?action=pending-approvals').then(r => r.json()).catch(() => []),
          fetch('/api/admin?action=advisor-campaigns').then(r => r.json()).catch(() => []),
          fetch('/api/admin?action=advisors').then(r => r.json()).catch(() => []),
        ]);
        setPendingApprovals(Array.isArray(approvalsResp) ? approvalsResp : []);
        setAdvisorCampaigns(Array.isArray(campaignsResp) ? campaignsResp : []);
        setAdvisors(Array.isArray(advisorsResp) ? advisorsResp : []);
      } catch { /* non-fatal */ }
      setLoading(false);
    }
    load();
  }, []);

  // ── Handlers ──
  const handleApproveLaunch = async (campaignId: string) => {
    setActionLoading(campaignId);
    try {
      await fetch('/api/admin?action=approve-launch', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId }),
      });
      setPendingApprovals(prev => prev.filter(p => p.id !== campaignId));
    } catch (err) {
      console.error('Failed to approve launch', err);
    }
    setActionLoading(null);
  };

  const handleRequestInfo = async () => {
    if (!requestInfoId || !requestInfoMsg.trim()) return;
    setActionLoading(requestInfoId);
    try {
      await fetch('/api/admin?action=request-info', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId: requestInfoId, message: requestInfoMsg }),
      });
      setRequestInfoId(null);
      setRequestInfoMsg('');
    } catch (err) {
      console.error('Failed to send info request', err);
    }
    setActionLoading(null);
  };

  const handleSendCoaching = async (campaignId: string, promptId: CoachingPromptId) => {
    setCoachingSending(true);
    const campaign = advisorCampaigns.find(c => c.id === campaignId);
    const rendered = renderPrompt(promptId, {
      creatorName: campaign?.user_id ?? 'Creator',
      campaignUrl: `https://fam.autos/goal/${campaignId}`,
      advisorName: 'Will',
    });
    try {
      await fetch('/api/admin?action=send-coaching', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId, subject: rendered.subject, body: rendered.body }),
      });
    } catch (err) {
      console.error('Failed to send coaching', err);
    }
    setCoachingSending(false);
    setCoachingCampaignId(null);
  };

  const handleAddAdvisor = async () => {
    if (!newAdvisor.clerkUserId.trim() || !newAdvisor.name.trim()) return;
    setAddingAdvisor(true);
    try {
      const resp = await fetch('/api/admin?action=add-advisor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAdvisor),
      });
      const data = await resp.json();
      if (data.advisor) {
        setAdvisors(prev => [...prev, data.advisor]);
      }
      setNewAdvisor({ clerkUserId: '', name: '', email: '', title: '' });
      setShowAddAdvisor(false);
    } catch (err) {
      console.error('Failed to add advisor', err);
    }
    setAddingAdvisor(false);
  };

  // ── Helpers ──
  const daysSince = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const activeCampaigns = advisorCampaigns.filter(c =>
    ['active', 'processing', 'processing_complete'].includes(c.status)
  );

  const awaitingSelection = advisorCampaigns.filter(c => c.status === 'offer_made');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-bold text-gray-900">Advisor</h2>

      {/* ── Sub-section 1: Pending Approvals ── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-amber-500" />
          <h3 className="text-base font-semibold text-gray-900">Pending Approvals</h3>
          {pendingApprovals.length > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
              {pendingApprovals.length}
            </span>
          )}
        </div>

        {pendingApprovals.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <p className="text-sm text-gray-400">No campaigns pending approval</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {pendingApprovals.map((approval) => {
              const days = daysSince(approval.created_at);
              return (
                <div key={approval.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{approval.title}</p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">{truncateId(approval.user_id)}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-gray-500">
                          FAM Score: <span className="font-semibold text-gray-800">{approval.fam_score ?? '—'}</span>
                        </span>
                        <span className="text-xs text-gray-500">
                          Down: <span className="font-semibold text-gray-800">
                            {approval.down_payment_amount ? formatDollars(approval.down_payment_amount) : '—'}
                          </span>
                        </span>
                        <span className="text-xs text-gray-500">
                          {days}d ago
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {approval.plaid_verified && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle className="w-3 h-3" /> Bank
                          </span>
                        )}
                        {approval.doc_verified && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle className="w-3 h-3" /> Docs
                          </span>
                        )}
                        {approval.address_verified && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle className="w-3 h-3" /> Address
                          </span>
                        )}
                        {(approval.social_accounts_count ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle className="w-3 h-3" /> {approval.social_accounts_count} social
                          </span>
                        )}
                        {!approval.plaid_verified && !approval.doc_verified && !approval.address_verified && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                            <AlertTriangle className="w-3 h-3" /> No verifications
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleApproveLaunch(approval.id)}
                        disabled={actionLoading === approval.id}
                        className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                      >
                        {actionLoading === approval.id ? 'Approving...' : 'Approve Launch'}
                      </button>
                      <button
                        onClick={() => setRequestInfoId(approval.id)}
                        className="px-3 py-1.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-200 transition-colors"
                      >
                        Request Info
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Sub-section 2: Active Campaigns Monitor ── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-indigo-500" />
          <h3 className="text-base font-semibold text-gray-900">Active Campaigns</h3>
          {activeCampaigns.length > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
              {activeCampaigns.length}
            </span>
          )}
        </div>

        {activeCampaigns.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <p className="text-sm text-gray-400">No active campaigns</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {activeCampaigns.map((campaign) => {
              const days = daysSince(campaign.created_at);
              const progressPct = campaign.goal_amount > 0
                ? (campaign.current_amount / campaign.goal_amount) * 100
                : 0;
              const expectedPct = (days / 7) * 100;
              const isStrong = progressPct >= expectedPct;

              return (
                <div key={campaign.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">{campaign.title}</p>
                        <StatusPill status={campaign.status} />
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>{campaign.supporter_count ?? 0} supporters</span>
                        <span>Day {days}</span>
                        <span className={`font-semibold ${isStrong ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {isStrong ? 'Strong' : 'At Risk'}
                        </span>
                      </div>

                      {/* Progress bar */}
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">
                            {formatDollars(campaign.current_amount)} / {formatDollars(campaign.goal_amount)}
                          </span>
                          <span className="font-semibold text-gray-700">{progressPct.toFixed(0)}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${isStrong ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min(100, progressPct)}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Coaching dropdown */}
                    <div className="relative shrink-0">
                      <button
                        onClick={() => setCoachingCampaignId(
                          coachingCampaignId === campaign.id ? null : campaign.id
                        )}
                        className="px-3 py-1.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-lg hover:bg-indigo-200 transition-colors flex items-center gap-1"
                      >
                        <Send className="w-3 h-3" /> Coach
                      </button>

                      {coachingCampaignId === campaign.id && (
                        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-10 py-1">
                          {Object.entries(COACHING_PROMPTS)
                            .filter(([key]) => key !== 'custom')
                            .map(([key, prompt]) => (
                              <button
                                key={key}
                                onClick={() => handleSendCoaching(campaign.id, key as CoachingPromptId)}
                                disabled={coachingSending}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 transition-colors disabled:opacity-50"
                              >
                                <p className="font-medium text-gray-800">{prompt.subject}</p>
                                <p className="text-gray-400 mt-0.5 truncate">{prompt.trigger}</p>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Sub-section 3: Awaiting Vehicle Selection ── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Car className="w-4 h-4 text-emerald-500" />
          <h3 className="text-base font-semibold text-gray-900">Awaiting Vehicle Selection</h3>
          {awaitingSelection.length > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
              {awaitingSelection.length}
            </span>
          )}
        </div>

        {awaitingSelection.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <p className="text-sm text-gray-400">No campaigns awaiting vehicle selection</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {awaitingSelection.map((campaign) => {
              const days = daysSince(campaign.created_at);
              const deadlineDays = Math.max(0, 14 - days);

              return (
                <div key={campaign.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{campaign.title}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{formatDollars(campaign.current_amount)} raised</span>
                        <span className={deadlineDays <= 3 ? 'text-red-500 font-semibold' : ''}>
                          {deadlineDays > 0 ? `${deadlineDays} days left to select` : 'Deadline passed'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSendCoaching(campaign.id, 'funded_select_vehicle')}
                      disabled={coachingSending}
                      className="px-3 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg hover:bg-emerald-200 disabled:opacity-50 transition-colors flex items-center gap-1"
                    >
                      <MessageSquare className="w-3 h-3" /> Nudge to Select
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Sub-section 4: Advisor Management ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-gray-500" />
            <h3 className="text-base font-semibold text-gray-900">Advisor Management</h3>
          </div>
          <button
            onClick={() => setShowAddAdvisor(!showAddAdvisor)}
            className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add Advisor
          </button>
        </div>

        {showAddAdvisor && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">New Advisor</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Clerk User ID *</label>
                <input
                  type="text"
                  value={newAdvisor.clerkUserId}
                  onChange={e => setNewAdvisor(prev => ({ ...prev, clerkUserId: e.target.value }))}
                  placeholder="user_2abc..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                <input
                  type="text"
                  value={newAdvisor.name}
                  onChange={e => setNewAdvisor(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Will"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={newAdvisor.email}
                  onChange={e => setNewAdvisor(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="will@fam.autos"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                <input
                  type="text"
                  value={newAdvisor.title}
                  onChange={e => setNewAdvisor(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="FAM Advisor"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setShowAddAdvisor(false); setNewAdvisor({ clerkUserId: '', name: '', email: '', title: '' }); }}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAdvisor}
                disabled={addingAdvisor || !newAdvisor.clerkUserId.trim() || !newAdvisor.name.trim()}
                className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {addingAdvisor ? 'Adding...' : 'Add Advisor'}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {advisors.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No advisors configured</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Email</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Title</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Campaigns</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {advisors.map((advisor) => (
                    <tr key={advisor.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">{advisor.name}</td>
                      <td className="px-4 py-3 text-gray-600">{advisor.email || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{advisor.title || '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{advisor.campaign_count ?? 0}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          advisor.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {advisor.active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Request Info Modal ── */}
      {requestInfoId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Request More Information</h3>
              <button
                onClick={() => { setRequestInfoId(null); setRequestInfoMsg(''); }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={requestInfoMsg}
              onChange={e => setRequestInfoMsg(e.target.value)}
              placeholder="What additional info do you need from the creator?"
              rows={4}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setRequestInfoId(null); setRequestInfoMsg(''); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestInfo}
                disabled={!requestInfoMsg.trim() || actionLoading === requestInfoId}
                className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
              >
                {actionLoading === requestInfoId ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

type NavSection = 'overview' | 'campaigns' | 'users' | 'financials' | 'dealers' | 'inventory' | 'prospecting' | 'advisor' | 'settings';

const NAV_ITEMS: { id: NavSection; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'financials', label: 'Financials', icon: DollarSign },
  { id: 'dealers', label: 'Dealers', icon: Store },
  { id: 'inventory', label: 'Inventory', icon: Car },
  { id: 'prospecting', label: 'Prospecting', icon: Radar },
  { id: 'advisor', label: 'Advisor', icon: UserCheck },
  { id: 'settings', label: 'Settings', icon: Settings },
];

// ---------------------------------------------------------------------------
// Main AdminDashboard
// ---------------------------------------------------------------------------

export function AdminDashboard() {
  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem('fam_admin_unlocked') === '1'
  );
  const [activeSection, setActiveSection] = useState<NavSection>('overview');

  const handleLock = () => {
    localStorage.removeItem('fam_admin_unlocked');
    setUnlocked(false);
  };

  if (!unlocked) {
    return <PinScreen onUnlock={() => setUnlocked(true)} />;
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'overview': return <OverviewSection />;
      case 'campaigns': return <CampaignsSection />;
      case 'users': return <UsersSection />;
      case 'financials': return <FinancialsSection />;
      case 'dealers': return <DealersSection />;
      case 'inventory': return <InventorySection />;
      case 'prospecting': return <DealerProspectingPanel />;
      case 'advisor': return <AdvisorSection />;
      case 'settings': return <SettingsSection />;
      default: return <OverviewSection />;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="sticky top-0 h-screen w-56 bg-white border-r border-gray-200 flex flex-col shrink-0">
        <div className="px-5 py-5 border-b border-gray-100">
          <h1 className="text-base font-bold text-gray-900">FAM Admin</h1>
          <p className="text-xs text-gray-400 mt-0.5">Super Dashboard</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeSection === id
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </button>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-gray-100">
          <button
            onClick={handleLock}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <Lock className="w-4 h-4 shrink-0" />
            Lock
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {renderSection()}
        </div>
      </main>
    </div>
  );
}
